import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Company-wide "Who's clocked in right now" roster.
 * Uses /api/dashboard/clocked-in which returns team members with details.
 */
export function ClockedInNowDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/dashboard/clocked-in`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        setMembers(Array.isArray(data) ? data : data.members || []);
      } catch {
        toast.error('Could not load the clocked-in roster');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open]);

  const fmtElapsed = (iso) => {
    if (!iso) return '';
    try {
      const ms = Date.now() - new Date(iso).getTime();
      const hrs = ms / 3600000;
      if (hrs < 1) return `${Math.round(hrs * 60)}m`;
      return `${hrs.toFixed(1)}h`;
    } catch {
      return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="clocked-in-now-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-green-600" />
            Clocked In Now
            {!loading && (
              <span className="text-sm font-normal text-gray-500">
                ({members.length})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin h-8 w-8 border-2 border-purple-600 border-t-transparent rounded-full"></div>
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p>No one is currently clocked in</p>
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((m, idx) => (
              <div
                key={m.employee_id || m.user_id || idx}
                className="bg-white border rounded-lg p-3 flex items-start gap-3"
                data-testid={`clocked-in-member-${idx}`}
              >
                <div className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-semibold flex-shrink-0">
                  {(m.name || m.employee_name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {m.name || m.employee_name || 'Unknown'}
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-2 flex-wrap mt-0.5">
                    {m.role && <span className="capitalize">{m.role.replace(/_/g, ' ')}</span>}
                    {m.project_name && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {m.project_name}
                      </span>
                    )}
                  </div>
                  {m.clock_in && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      Since {new Date(m.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {fmtElapsed(m.clock_in)}
                    </div>
                  )}
                </div>
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse mt-1.5 flex-shrink-0"></div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ClockedInNowDialog;
